<template>
  <div id="blog-listing" class="w-100 flex flex-column pt60">
    <div v-if="loading" class="fixed top-0 left-0 w-100 h-100 flex middle-xs center-xs z-1000" style="background: rgba(0, 0, 0, 0.5);">
      <div class="flex middle-xs cl-white" style="font-size: 24px; text-align: center;">
        <div class="spinner mr10"></div>
        <span>{{ $t('Loading...') }}</span>
      </div>
    </div>

    <div v-else class="w-100">
      <!-- Breadcrumbs -->
      <div class="container py15">
        <breadcrumbs :routes="breadcrumbs" :active-route="currentCategory.name" />
      </div>

      <!-- Blog Header -->
      <div class="container">
        <h1 class="blog-title mb30">
          {{ currentCategory ? currentCategory.name : $t('Blog Category') }}
        </h1>
        <!-- <p v-if="currentCategory && currentCategory.description" class="category-description mb30">
          {{ currentCategory.description }}
        </p> -->
      </div>

      <!-- Blog Articles -->
      <div class="container">
        <div v-if="articles.length" class="blog-articles-list">
          <article
            v-for="article in articles"
            :key="article.slug"
            class="blog-article-row mb20"
          >
            <router-link :to="getArticleUrl(article)" class="article-link">
              <div class="row middle-xs">
                <!-- Preview Image -->
                <div class="col-md-4 col-sm-5 col-xs-12 ">
                  <div class="article-image-container">
                    <img
                      v-if="article.previewImageUrl"
                      :src="article.previewImageUrl"
                      :alt="article.previewImageAlt || article.title"
                      class="article-preview-image"
                    />
                    <div v-else class="article-image-placeholder">
                      <span>{{ $t('No Image') }}</span>
                    </div>
                  </div>
                </div>

                <!-- Article Content -->
                <div class="col-md-8 col-sm-7 col-xs-12">
                  <div class="article-content">
                    <h2 class="article-title mb15">{{ article.title }}</h2>
                    <p class="article-excerpt mb20">{{ article.excerpt }}</p>
                    <span class="read-more-link">
                      {{ $t('Read more') }} →
                    </span>
                  </div>
                </div>
              </div>
            </router-link>
          </article>
        </div>

        <div v-else class="no-articles">
          <p>{{ $t('No articles found in this category') }}.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Logger } from '@vue-storefront/core/lib/logger'
import { getApolloProvider } from '@vue-storefront/core/scripts/resolvers/resolveGraphQL'
import Breadcrumbs from 'theme/components/core/Breadcrumbs'
import BLOG_ARTICLES_BY_CATEGORY_QUERY from '../../graphql/strapi/queries/blogArticlesByCategory.gql'
import BLOG_CATEGORY_QUERY from '../../graphql/strapi/queries/blogCategory.gql'
import { mapState } from 'vuex'
import { getCmsPartnerId } from '../../helpers'

export default {
  name: 'BlogListing',
  components: {
    Breadcrumbs
  },
  data () {
    return {
      loading: true,
      articles: [],
      currentCategory: null
    }
  },
  computed: {
    ...mapState({
      storeView: state => state.storeView
    }),
    cmsPartnerId () {
      return getCmsPartnerId(this.storeView);
    },
    categorySlug () {
      return this.$route.params.categorySlug
    },
    breadcrumbs () {
      const baseBreadcrumbs = [
        {
          name: this.$t('Home'),
          route_link: '/'
        },
        {
          name: this.$t('Blog'),
          route_link: '/blog'
        }
      ]
      return baseBreadcrumbs
    }
  },
  methods: {
    extractTextFromContent (pageContent) {
      if (!pageContent || !Array.isArray(pageContent)) {
        return ''
      }

      // Extract text from the first few content blocks
      let extractedText = ''
      let wordCount = 0
      const maxWords = 30

      for (const block of pageContent) {
        if (wordCount >= maxWords) break
        console.log('Block', block)
        if (block.__typename === 'ComponentStaticPageMarkdown') {
          if (block.Markdown) {
            const words = block.Markdown.split(' ')
            const remainingWords = maxWords - wordCount

            if (words.length <= remainingWords) {
              extractedText += block.Markdown + ' '
              wordCount += words.length
            } else {
              extractedText += words.slice(0, remainingWords).join(' ') + '...'
              wordCount = maxWords
              break
            }
          }
        }
      }

      return extractedText.trim()
    },
    async fetchCategoryAndArticles (partnerId) {
      try {
        this.loading = true

        // Fetch category details
        const categoryResponse = await this.$apollo.query({
          query: BLOG_CATEGORY_QUERY,
          variables: { slug: this.categorySlug }
        })

        if (categoryResponse.data.blogCategories.data.length === 0) {
          // Category not found, redirect to categories page
          this.$router.push('/blog')
          return
        }

        this.currentCategory = {
          name: categoryResponse.data.blogCategories.data[0].attributes.name,
          slug: categoryResponse.data.blogCategories.data[0].attributes.slug,
          description: categoryResponse.data.blogCategories.data[0].attributes.description
        }

        // Fetch articles for this category
        const articlesResponse = await this.$apollo.query({
          query: BLOG_ARTICLES_BY_CATEGORY_QUERY,
          variables: { categorySlug: this.categorySlug, partnerId, sort: ['createdAt:desc'] }
        })

        this.articles = articlesResponse.data.blogArticles.data.map(article => {
          const excerpt = this.extractTextFromContent(article.attributes.PageContent) ||
                         article.attributes.MetaDescription ||
                         'No preview available...'

          return {
            title: article.attributes.Title,
            metaDescription: article.attributes.MetaDescription,
            slug: article.attributes.slug,
            excerpt: excerpt,
            previewImageUrl: article.attributes.previewImage?.data?.attributes?.url,
            previewImageAlt: article.attributes.previewImage?.data?.attributes?.alternativeText
          }
        })

        this.loading = false
      } catch (error) {
        Logger.error('Cannot load blog category or articles: ' + error)
        this.loading = false
        // Redirect to categories page on error
        this.$router.push('/blog')
      }
    },
    getArticleUrl (article) {
      return `/blog/${this.categorySlug}/${article.slug}`
    }
  },
  watch: {
    '$route': {
      handler () {
        if (this.categorySlug) {
          this.fetchCategoryAndArticles(this.cmsPartnerId)
        }
      },
      immediate: true
    }
  },
  mounted () {
    if (this.categorySlug) {
      this.fetchCategoryAndArticles(this.cmsPartnerId)
    }
  },
  serverPrefetch () {
    if (this.categorySlug) {
      return this.fetchCategoryAndArticles(this.cmsPartnerId)
    }
  },
  async asyncData ({ store, context, route }) {
    if (context) context.output.cacheTags.add(`blog`)
    Logger.info('Calling asyncData in BlogListing')()
    const categorySlug = route.params.categorySlug
    const partnerId = getCmsPartnerId(store.state.storeView)

    try {
      // Get Apollo client for server-side
      const apolloProvider = await getApolloProvider()
      const apolloClient = apolloProvider.defaultClient

      // Fetch category details
      const categoryResponse = await apolloClient.query({
        query: BLOG_CATEGORY_QUERY,
        variables: { slug: categorySlug }
      })

      if (categoryResponse.data.blogCategories.data.length === 0) {
        return { articles: [], currentCategory: null, loading: false }
      }

      const currentCategory = {
        name: categoryResponse.data.blogCategories.data[0].attributes.name,
        slug: categoryResponse.data.blogCategories.data[0].attributes.slug,
        description: categoryResponse.data.blogCategories.data[0].attributes.description
      }

      // Fetch articles for this category
      const articlesResponse = await apolloClient.query({
        query: BLOG_ARTICLES_BY_CATEGORY_QUERY,
        variables: { categorySlug, partnerId, sort: ['createdAt:desc'] }
      })

      const articles = articlesResponse.data.blogArticles.data.map(article => {
        // Extract text from content for excerpt
        let excerpt = ''
        if (article.attributes.PageContent && Array.isArray(article.attributes.PageContent)) {
          let wordCount = 0
          const maxWords = 30

          for (const block of article.attributes.PageContent) {
            if (wordCount >= maxWords) break

            if (block.type === 'paragraph' && block.children) {
              for (const child of block.children) {
                if (child.text) {
                  const words = child.text.split(' ')
                  const remainingWords = maxWords - wordCount

                  if (words.length <= remainingWords) {
                    excerpt += child.text + ' '
                    wordCount += words.length
                  } else {
                    excerpt += words.slice(0, remainingWords).join(' ') + '...'
                    wordCount = maxWords
                    break
                  }
                }
              }
            }
          }
        }

        if (!excerpt) {
          excerpt = article.attributes.MetaDescription || 'No preview available...'
        }

        return {
          title: article.attributes.Title,
          metaDescription: article.attributes.MetaDescription,
          slug: article.attributes.slug,
          excerpt: excerpt.trim(),
          previewImageUrl: article.attributes.previewImage?.data?.attributes?.url,
          previewImageAlt: article.attributes.previewImage?.data?.attributes?.alternativeText
        }
      })

      return {
        articles,
        currentCategory,
        loading: false
      }
    } catch (error) {
      Logger.error('Error in asyncData for BlogListing: ' + error)()
      return { articles: [], currentCategory: null, loading: false }
    }
  },
  metaInfo () {
    return {
      title: this.currentCategory ? this.currentCategory.name : this.$t('Blog Category'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.currentCategory && this.currentCategory.description
            ? this.currentCategory.description
            : this.$t('Blog Category')
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
.blog-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #333;

  @media (max-width: 767px) {
    font-size: 2rem;
  }

  @media (max-width: 575px) {
    font-size: 1.75rem;
  }
}

.category-description {
  font-size: 1.125rem;
  color: #666;
  line-height: 1.6;

  @media (max-width: 575px) {
    font-size: 1rem;
  }
}

.blog-articles-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 575px) {
    gap: 1rem;
  }
}

.blog-article-row {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  background: white;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  .article-link {
    display: block;
    text-decoration: none;
    color: inherit;
    width: 100%;
    height: 100%;
  }

  .row {
    margin: 0;
    width: 100%;
    display: flex;
    align-items: stretch;
    min-height: 200px;

    @media (max-width: 575px) {
      flex-direction: column;
      min-height: auto;
    }
  }

  .col-md-4, .col-sm-5, .col-xs-12 {
    padding: 0;
    display: flex;
    align-items: stretch;

    @media (max-width: 767px) {
      margin-bottom: 0;
    }

    @media (max-width: 575px) {
      margin-bottom: 0;
    }
  }

  .col-md-8, .col-sm-7 {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;

    @media (max-width: 767px) {
      padding: 1.25rem;
    }

    @media (max-width: 575px) {
      padding: 1rem;
    }
  }
}

.article-image-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px 0 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    height: 180px;
    border-radius: 8px 8px 0 0;
  }

  @media (max-width: 575px) {
    height: 160px;
    border-radius: 8px 8px 0 0;
  }
}

.article-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  .blog-article-row:hover & {
    transform: scale(1.05);
  }
}

.article-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--partner);
  color: white;
  font-size: 1rem;

  @media (max-width: 575px) {
    font-size: 0.875rem;
  }
}

.article-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
}

.article-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #333;
  line-height: 1.3;
  text-decoration: none;
  transition: color 0.3s ease;

  .blog-article-row:hover & {
    color: var(--partner);
  }

  @media (max-width: 767px) {
    font-size: 1.25rem;
  }

  @media (max-width: 575px) {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }
}

.article-excerpt {
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.25rem;
  line-height: 1.6;
  flex-grow: 1;

  @media (max-width: 575px) {
    font-size: 0.875rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }
}

.read-more-link {
  font-size: 1rem;
  color: var(--partner);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  margin-top: auto;
  transition: all 0.3s ease;

  .blog-article-row:hover & {
    transform: translateX(4px);
  }

  @media (max-width: 575px) {
    font-size: 0.875rem;
  }
}

.no-articles {
  text-align: center;
  padding: 3rem 0;

  @media (max-width: 575px) {
    padding: 2rem 0;
  }

  p {
    font-size: 1.125rem;
    color: #666;
    margin-bottom: 1.5rem;

    @media (max-width: 575px) {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
  }

  .btn-primary {
    display: inline-block;
    padding: 0.75rem 1.5rem;
    background: var(--partner);
    color: white;
    text-decoration: none;
    border-radius: 4px;
    transition: background 0.3s ease;

    &:hover {
     opacity: 0.8;
    }

    @media (max-width: 575px) {
      padding: 0.625rem 1.25rem;
      font-size: 0.875rem;
    }
  }
}

// Ensure consistent spacing and layout
.mb40 {
  margin-bottom: 1.5rem !important;
}

.mb20 {
  margin-bottom: 1.25rem !important;

  @media (max-width: 575px) {
    margin-bottom: 1rem !important;
  }
}

.mb15 {
  margin-bottom: 0.9375rem !important;

  @media (max-width: 575px) {
    margin-bottom: 0.75rem !important;
  }
}

// Override any conflicting grid styles
.row.middle-xs {
  align-items: stretch !important;
}

// Container padding adjustments for small screens
.container {
  @media (max-width: 575px) {
    padding-left: 15px;
    padding-right: 15px;
  }
}
</style>
